import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { X, Clock, DollarSign, Search, Building2 } from "lucide-react";
import { useServicesByPractice } from "@/hooks/useServices";
import { useAuth } from "@/hooks/useAuth";

export default function ServiceSelectionModal({
  allPractices = [],
  services = [], // For doctor usage (legacy support)
  onSelect,
  onClose,
}) {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPractice, setSelectedPractice] = useState("");

  // Determine if this is a patient (needs practice selection) or doctor (uses their practice)
  const isPatient = user?.userType === "patient";
  const doctorPracticeId = user?.practiceId;

  // For patients: Get services for selected practice
  // For doctors: Use services from their practice (passed as prop) or fetch their practice services
  const { data: patientServices = [] } = useServicesByPractice(selectedPractice);
  
  // Determine which services to use
  const availableServices = isPatient 
    ? patientServices 
    : services.length > 0 
      ? services 
      : patientServices;

  const filteredServices = availableServices.filter(
    (service) =>
      service.service_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (service.description &&
        service.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleServiceSelect = (service) => {
    if (isPatient) {
      // For patients: Include practice information with the selected service
      const serviceWithPractice = {
        ...service,
        selectedPracticeId: selectedPractice,
        selectedPracticeName: allPractices.find(p => p.practiceId === selectedPractice)?.practiceName
      };
      onSelect(serviceWithPractice);
    } else {
      // For doctors: Include their practice information
      const serviceWithPractice = {
        ...service,
        selectedPracticeId: doctorPracticeId,
        selectedPracticeName: user?.practiceName || "Current Practice"
      };
      onSelect(serviceWithPractice);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl bg-white/90 backdrop-blur-xl border-slate-200/60 shadow-2xl max-h-[90vh] flex flex-col">
        <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-white/80 backdrop-blur-sm z-10 border-b">
          <div>
            <CardTitle>Select a Service</CardTitle>
            <p className="text-sm text-slate-500">
              {isPatient 
                ? "Choose a practice and service to schedule an appointment for."
                : "Choose a service to schedule an appointment for."
              }
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <div className="p-6 border-b space-y-4">
          {/* Practice Selection - Only for Patients */}
          {isPatient && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-slate-500" />
                <span className="text-sm font-medium text-slate-700">Practice:</span>
              </div>
              <Select value={selectedPractice} onValueChange={setSelectedPractice}>
                <SelectTrigger className="w-64 bg-white border-slate-300">
                  <SelectValue placeholder={
                    allPractices.length === 0 
                      ? "No active practices with services available"
                      : "Choose a practice..."
                  } />
                </SelectTrigger>
                <SelectContent>
                  {allPractices.length === 0 ? (
                    <SelectItem value="no-practices" disabled>
                      No active practices with services available
                    </SelectItem>
                  ) : (
                    allPractices.map((practice) => (
                      <SelectItem key={practice.practiceId} value={practice.practiceId}>
                        {practice.practiceName}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Service Search */}
          {(isPatient ? selectedPractice : true) && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Search services..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          )}
        </div>
        <CardContent className="p-6 overflow-y-auto">
          {isPatient && !selectedPractice ? (
            <div className="text-center py-12 text-slate-500">
              <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>{
                allPractices.length === 0
                  ? "No active practices with services available."
                  : "Please select a practice to view available services."
              }</p>
            </div>
          ) : filteredServices.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredServices.map((service, index) => (
                <Card
                  key={service.id}
                  className="bg-white hover:shadow-lg transition-shadow cursor-pointer flex flex-col"
                  onClick={() => handleServiceSelect(service)}
                  data-testid={`service-card-${index}`}
                >
                  <CardHeader>
                    <CardTitle className="text-base">
                      {service.service_name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex-grow">
                    <p className="text-xs text-slate-600 mb-4 line-clamp-3">
                      {service.description}
                    </p>
                    <div className="flex justify-between items-center text-xs text-slate-500 pt-2 border-t">
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>{service.duration_minutes} min</span>
                      </div>
                      <div className="flex items-center gap-1 font-medium text-slate-700">
                        <DollarSign className="w-3 h-3" />
                        <span>{service.base_price?.toFixed(2) || "N/A"}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <p>No services available for the selected practice.</p>
              {searchTerm && <p className="text-sm mt-2">Try adjusting your search terms.</p>}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
